import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "store";

export interface PaperQuote {
  quote: string;
  quoteIndex: number;
  source?: "abstract" | "full-text";
  section?: string;
}

interface InteractionCitation {
  interactionId: string;
  citationCount: number;
  quotes: PaperQuote[];
  firstCitedAt: string;
}

interface PaperCitation {
  paperId: string;
  interactions: Record<string, InteractionCitation>;
  totalCitationCount: number;
  firstCitedAt: string;
}

interface CitedPapersState {
  citations: Record<string, PaperCitation>;
}

const initialState: CitedPapersState = {
  citations: {},
} as CitedPapersState;

interface AddCitationPayload {
  paperId: string;
  interactionId: string;
  quote?: string;
  quoteIndex?: number;
  source?: "abstract" | "full-text";
  section?: string;
}

const citedPapersSlice = createSlice({
  name: "citedPapers",
  initialState,
  reducers: {
    addCitation(
      state: CitedPapersState,
      { payload }: PayloadAction<AddCitationPayload>
    ) {
      const { paperId, interactionId, quote, quoteIndex, source, section } =
        payload;

      // Initialize paper if new
      if (!state.citations[paperId]) {
        state.citations[paperId] = {
          paperId,
          interactions: {},
          totalCitationCount: 0,
          firstCitedAt: new Date().toISOString(),
        };
      }

      // Initialize interaction if new
      if (!state.citations[paperId].interactions[interactionId]) {
        state.citations[paperId].interactions[interactionId] = {
          interactionId,
          citationCount: 0,
          quotes: [],
          firstCitedAt: new Date().toISOString(),
        };
      }

      const interaction = state.citations[paperId].interactions[interactionId];

      // Handle quote tracking
      if (quote && typeof quoteIndex === "number") {
        // Ensure quotes array is large enough (handles out-of-order mounting)
        while (interaction.quotes.length <= quoteIndex) {
          interaction.quotes.push({
            quote: "",
            quoteIndex: interaction.quotes.length,
          });
        }

        // Only update if the quote is different (idempotent)
        const existingQuote = interaction.quotes[quoteIndex];
        if (existingQuote.quote !== quote) {
          interaction.quotes[quoteIndex] = {
            quote,
            quoteIndex,
            source,
            section,
          };

          // Only increment citation count when actually adding/changing a quote
          if (!existingQuote.quote) {
            interaction.citationCount++;
            state.citations[paperId].totalCitationCount++;
          }
        }
      } else {
        // For non-quote citations, only increment if this is the first time
        // Check if we've already counted a basic citation for this interaction
        const hasBasicCitation =
          interaction.citationCount >
          interaction.quotes.filter((q) => q.quote).length;

        if (!hasBasicCitation) {
          interaction.citationCount++;
          state.citations[paperId].totalCitationCount++;
        }
      }
    },

    clearCitedPapers(state: CitedPapersState) {
      state.citations = {};
    },
  },
});

// Backwards-compatible selectors
export const selectCitedPapers = (
  state: RootState
): Record<string, boolean> => {
  const citations = state.citedPapers.citations;
  return Object.keys(citations).reduce(
    (acc, paperId) => {
      acc[paperId] = true;
      return acc;
    },
    {} as Record<string, boolean>
  );
};

export const selectCitedPaperIds = (state: RootState): string[] => {
  return Object.keys(state.citedPapers.citations);
};

export const selectQuotesForPaper = (
  state: RootState,
  interactionId: string,
  paperId: string
): PaperQuote[] => {
  return (
    state.citedPapers.citations[paperId]?.interactions[interactionId]?.quotes ||
    []
  );
};

export const selectPaperCitationCount = (
  state: RootState,
  paperId: string
): number => {
  return state.citedPapers.citations[paperId]?.totalCitationCount || 0;
};

export const selectInteractionCitations = (
  state: RootState,
  interactionId: string
): Record<string, InteractionCitation> => {
  const result: Record<string, InteractionCitation> = {};
  Object.entries(state.citedPapers.citations).forEach(([paperId, citation]) => {
    if (citation.interactions[interactionId]) {
      result[paperId] = citation.interactions[interactionId];
    }
  });
  return result;
};

export const selectQuotesForInteraction = (
  state: RootState,
  interactionId: string
): Record<string, PaperQuote[]> => {
  const result: Record<string, PaperQuote[]> = {};
  Object.entries(state.citedPapers.citations).forEach(([paperId, citation]) => {
    if (citation.interactions[interactionId]) {
      result[paperId] = citation.interactions[interactionId].quotes;
    }
  });
  return result;
};

export const { addCitation, clearCitedPapers } = citedPapersSlice.actions;

export default citedPapersSlice.reducer;
